import React, { useState } from 'react';
import styled, { withTheme } from 'styled-components';
import { withBreakpoints } from 'react-breakpoints';
import helpers from 'helpers';
import { AbstractChart } from 'containers/charts/AbstractChart';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import FilterBar from 'components/atoms/tables/FilterBar';
import FilterLabel from 'components/atoms/tables/FilterLabel';
import ToggleSwitch from 'components/atoms/form/ToggleSwitch';
import { getPointWidth } from 'helpers/labs/charts';

const ChartCallout = styled.div`
    width: 100%;
    text-align: center;
    margin-bottom: 15px;
    padding: 15px;
    @media (max-width: 768px) {
        font-size: 13px;
        line-height: 25px;
    }

    b {
        font-weight: bold;
    }
`;

const Badge = styled.span`
    font-size: 1.2em; 
    font-weight: bold;
    padding: 5px;
    border-radius: 10px;
    color: #007DC6;

    @media (max-width: 768px) {
        font-size: 13px;
    }
`;

const ATVChannelLift = (props) => {
    const [includeVideo, setIncludeVideo] = useState(false);

    const {
        hasVideoData,
        hasATVData,
        hasATVLift,
        hasATVAndVideoLift,
        ATVLiftPercentage,
        ATVAndVideoLiftPercentage,
        nonATVConversionRate,
        nonATVOrVideoConversionRate,
        combinatorialATVConversionRate,
        combinatorialATVAndVideoConversionRate,
        currentBreakpoint,
        animation = true
    } = props;
    const liftPercentage = includeVideo ? ATVAndVideoLiftPercentage : ATVLiftPercentage;
    const combinatorialConversionRate = includeVideo
        ? combinatorialATVAndVideoConversionRate
        : combinatorialATVConversionRate;
    const nonConversionRate = includeVideo ? nonATVOrVideoConversionRate : nonATVConversionRate;
    const categories = ['Households without ATV', 'Households with ATV'];
    const hasLift = hasATVLift || (includeVideo && hasATVAndVideoLift);
    const hasData = hasATVData || (includeVideo && hasVideoData);
    const series = [
        {
            name: 'Households with ATV',
            data: [
                null,
                combinatorialConversionRate - nonConversionRate,
            ],
            color: '#007DC6',
            tooltip: {
                pointFormatter() {
                    return `Conversion Rate: ${combinatorialConversionRate.toFixed(2)}%<br/>
                        Lift in Conversion Rate: ${liftPercentage}%`;
                }
            }
        },
        {
            name: 'Households without ATV',
            data: [nonConversionRate, nonConversionRate],
            color: '#ccc',
            tooltip: {
                pointFormatter() {
                    return `Conversion Rate: ${nonConversionRate.toFixed(2)}%`;
                }
            }
        },
    ];

    const BlockWrapper = styled.div`
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 5px;
        margin: 10px;
        flex-direction: column;
    `;

    const handleVideoToggle = () => {
        setIncludeVideo(!includeVideo);
    };

    const NoATVDataBlock = <BlockWrapper>
        <h3><b>We couldn't detect any ATV data</b></h3>
        {hasVideoData && <p>Are your ATV campaigns classified as Video, perhaps?
            If so, enable the option above to include Video as part of ATV.</p>
        }
    </BlockWrapper>;

    const NoLiftBlock = <BlockWrapper>
        <h3><b>Insufficient data to visualize lift</b></h3>
        <p>We could not visualize lift based on the data provided.</p>
    </BlockWrapper>;

    return (
        <ContentTile
            padding={helpers.contentPadding('chart')}
        >
            <ContentHeader type={'chart'} hideCSVExport={true}>
                {props.title}
            </ContentHeader>
            {hasVideoData && <FilterBar margin="-8px 0 10px 0" data-html2canvas-ignore>
                <ToggleSwitch onClick={handleVideoToggle} value={includeVideo} scale="0.5" />
                <FilterLabel>INCLUDE VIDEO AS PART OF ATV</FilterLabel>
            </FilterBar>}
            {(!hasATVData && !includeVideo) && NoATVDataBlock}
            {(!hasLift && hasData) && NoLiftBlock}
            { hasLift &&
                <React.Fragment>
                    {includeVideo ? (
                        <ChartCallout>In households where Advanced TV and Video have run alongside other
                            tactics, the data shows a<Badge>{`${liftPercentage}%`}</Badge>lift in conversion rate
                            over households where no video or ATV ads have run.
                        </ChartCallout>
                    ) : (
                        <ChartCallout>In households where Advanced TV has run alongside other
                        tactics, the data shows a<Badge>{`${liftPercentage}%`}</Badge>lift in conversion rate.
                        </ChartCallout>
                    )}
                    <AbstractChart
                        exportName={props.exportName}
                        config={{
                            chart: {
                                type: 'column',
                            },
                            tooltip: { enabled: false },
                            title: {
                                text: null,
                            },
                            legend: {
                                enabled: false,
                            },
                            xAxis: {
                                categories,
                                labels: {
                                    style: {
                                        color: 'gray',
                                        fontSize: '14px'
                                    },
                                }
                            },
                            yAxis: {
                                gridLineDashStyle: 'dash',
                                title: {
                                    text: 'Conversion Rate %',
                                },
                                stackLabels: {
                                    enabled: true,
                                    style: {
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        color: 'gray',
                                    },
                                    formatter() {
                                        return `${this.total.toPrecision(3)}%`;
                                    },
                                },
                            },
                            exporting: {
                                enabled: false,
                            },
                            plotOptions: {
                                column: {
                                    stacking: 'normal',
                                    depth: 150,
                                    dataLabels: {

                                        enabled: true,
                                    },
                                },
                                series: {
                                    name: 'No ATV',
                                    stacking: 'normal',
                                    animation,
                                    pointWidth: getPointWidth(currentBreakpoint),
                                    enableMouseTracking: false,
                                    dataLabels: {
                                        enabled: true,
                                        style: {
                                            fontWeight: 'bold',
                                            color: '#fff',
                                            textOutline: 0,
                                            fontSize: '14px',
                                        },
                                        format: null,
                                        formatter() {
                                            if (this.series.index > 0) {
                                                return null;
                                            }
                                            return '⬆ ATV Lift';
                                        },
                                        align: 'center'
                                    },
                                },
                            },
                            series,
                            credits: {
                                enabled: false,
                            },
                        }}
                    />
                </React.Fragment>
            }
        </ContentTile>
    );
};

export default withTheme(withBreakpoints(ATVChannelLift));



// WEBPACK FOOTER //
// ./src/components/molecules/charts/labs/ATVLift.js